import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { submitPublicFeedback } from "../utils/publicFeedbackSubmit";
import { formatTimeRange } from "../utils/openHouseHelpers";

export default function PublicFeedback() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const token = params.get("t");
  const addressFromUrl = params.get("a") || "";
  const dateFromUrl = params.get("d") || "";
  const startFromUrl = params.get("s") || "";
  const endFromUrl = params.get("e") || "";
  const timeFromUrl = startFromUrl && endFromUrl
    ? formatTimeRange(startFromUrl, endFromUrl)
    : "";

  const [agentName, setAgentName] = useState("");
  const [date, setDate] = useState(dateFromUrl);
  const [time, setTime] = useState(timeFromUrl);
  const [groups, setGroups] = useState("");
  const [secondShowings, setSecondShowings] = useState("");
  const [agentFeedback, setAgentFeedback] = useState("");
  const [buyerFeedback, setBuyerFeedback] = useState("");
  const [otherComments, setOtherComments] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id || !token) setError("This link is missing required information.");
  }, [id, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agentName || !date || !time || groups === "" || secondShowings === "" || !agentFeedback || !buyerFeedback) {
      setError("Please fill in all required fields.");
      return;
    }
    setError("");
    setSubmitting(true);
    const r = await submitPublicFeedback({
      id, token,
      feedback: {
        agentName, date, time,
        groups: Number(groups),
        secondShowings: Number(secondShowings),
        agentFeedback, buyerFeedback,
        otherComments: otherComments || "",
      },
    });
    setSubmitting(false);
    if (r.ok) setSubmitted(true);
    else setError(r.error || "This link is no longer valid.");
  };

  const BrandHeader = () => (
    <div className="bg-gradient-to-b from-[#0f172a] to-[#1E293B] px-8 py-7 text-center">
      <img
        src="/assets/email/te-logo-white.png"
        alt="Team Edelstein"
        className="h-10 mx-auto object-contain"
      />
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <BrandHeader />
          <div className="p-10 text-center">
            <div className="w-14 h-14 bg-emerald-500 text-white rounded-full mx-auto mb-4 flex items-center justify-center text-2xl">✓</div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Feedback received</h1>
            <p className="text-sm text-slate-500">Thanks for sharing what you heard at the open house. The seller will receive a recap shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !id) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <BrandHeader />
          <div className="p-10 text-center">
            <h1 className="text-xl font-bold text-slate-900 mb-2">Link no longer valid</h1>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const labelClasses = "block text-xs font-semibold text-slate-700 mb-1";
  const inputClasses = "w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-slate-400";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <BrandHeader />
        <div className="px-8 py-6 border-b border-slate-100 text-center">
          <h1 className="text-xl font-bold text-slate-900">Open House Feedback</h1>
          {(addressFromUrl || timeFromUrl) && (
            <p className="text-xs text-slate-500 mt-1">
              {addressFromUrl}{addressFromUrl && timeFromUrl ? " · " : ""}{timeFromUrl}
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
          <p className="text-sm text-slate-600">
            Thanks for hosting! Please share what you heard at the open house. The seller will receive a recap.
          </p>

          <div>
            <label className={labelClasses}>Agent First and Last Name *</label>
            <input value={agentName} onChange={(e) => setAgentName(e.target.value)} className={inputClasses} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses}>Date *</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className={labelClasses}>Time *</label>
              <input type="text" value={time} onChange={(e) => setTime(e.target.value)} placeholder="e.g. 1:00pm – 3:00pm" className={inputClasses} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses}>Number of Groups *</label>
              <input type="number" min="0" value={groups} onChange={(e) => setGroups(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className={labelClasses}>Potential Second Showings *</label>
              <input type="number" min="0" value={secondShowings} onChange={(e) => setSecondShowings(e.target.value)} className={inputClasses} />
            </div>
          </div>
          <div>
            <label className={labelClasses}>Agent's Favorable / Unfavorable Feedback *</label>
            <textarea rows={3} value={agentFeedback} onChange={(e) => setAgentFeedback(e.target.value)} className={inputClasses} />
          </div>
          <div>
            <label className={labelClasses}>Buyers' Favorable / Unfavorable Feedback *</label>
            <textarea rows={3} value={buyerFeedback} onChange={(e) => setBuyerFeedback(e.target.value)} className={inputClasses} />
          </div>
          <div>
            <label className={labelClasses}>Any Other Comments</label>
            <textarea rows={2} value={otherComments} onChange={(e) => setOtherComments(e.target.value)} className={inputClasses} />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-b from-[#0f172a] to-[#1E293B] hover:opacity-90 disabled:opacity-50 text-white font-semibold py-3 rounded-lg shadow-md"
          >
            {submitting ? "Submitting…" : "Submit Feedback"}
          </button>
        </form>
      </div>
    </div>
  );
}
